import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchPasswordData, resetPassword, resetPostCodeData } from "../store/actions";
import { Popup } from "../../dashboard/component";
import { closeIcon } from "../../../assets/icons";
import { requestMFACodeOverEmailRequest, resetRequestMFACodeOverEmail } from "../store/slice/requestMFACode.slice";
import { setData, TOKEN, USER_DATA } from "../../../utils/Storage";
import { isLogin } from "../../../store/actions/ui-actions";
import moment from "moment";
import * as jose from "jose";


export enum ContentType {
    PASSCODE = "PASSCODE",
    EMAIL_MFA = "EMAIL_MFA",
    PASSWORD = "PASSWORD"
}


interface AuthIdentificationInterface {
    contentType: ContentType;
    tempToken: string; // This is an optional token for password reset

    // This is an optional array to render the secondary authentication part
    // e.g. if the contentType is PASSCODE, then this array will contain the
    // parts of the passcode that need to be entered by the user.
    secondaryAuthenticationPart?: string[];
    onClose: () => void;
}

const AuthIdentificationModal: React.FC<AuthIdentificationInterface> = ({
    contentType,
    tempToken,
    secondaryAuthenticationPart = [],
    onClose
}) => {
    const refEle: any = useRef(null);
    const dispatch = useDispatch();

    const passCodeData: any = useSelector<any>((state) => state.passcodeData);
    const requestMFAOverEmailCodeData: any = useSelector<any>((state) => state.requestMFAOverEmailCodeData);


    const [resendRequest, setResendRequest] = useState<boolean>(false);
    const [token, setToken] = useState<string>(tempToken);

    useEffect(() => {
        if (requestMFAOverEmailCodeData.isSuccess && requestMFAOverEmailCodeData.data !== null) {
            toast.success("A one time login code has been sent to your registered email address.");
            setToken(requestMFAOverEmailCodeData.data.data.token);
            dispatch(resetRequestMFACodeOverEmail());
        } else if (requestMFAOverEmailCodeData.isError) {
            toast.error("Internal Server Error")
            dispatch(resetRequestMFACodeOverEmail());
        }


        if (passCodeData.isSuccess && passCodeData.data !== null) {
            const token = passCodeData.data.coreJwtToken;
            const jwtToken: any = jose.decodeJwt(token);
            const epoch = jwtToken?.exp.slice(0, -3)
            const expiryDate = moment.unix(jwtToken?.exp.slice(0, -3)).format("YYYY-MM-DD HH:mm:ss");

            setData(USER_DATA, { ...passCodeData.data, mfa: contentType })
            setData(TOKEN, { token: passCodeData.data.coreJwtToken, provider: 'legacy', expiryDate, epoch });

            dispatch(isLogin({ token: passCodeData.data.coreJwtToken, isLegacy: true, provider: "legacy" }))
            dispatch(resetPostCodeData());

            window.location.reload();
        } else if (passCodeData.isError) {
            toast.error("Internal Server Error")
            dispatch(resetPostCodeData());
        }
    }, [requestMFAOverEmailCodeData, passCodeData]);



    // Button Event Handlers
    const __submitPassCode = (e: any) => {
        e.preventDefault();

        const formData = new FormData(e.target);
        const passCode = Array.from(formData.values()).join('');

        dispatch(fetchPasswordData({
            secondaryAuthenticationPart: passCode,
            userToken: token
        }));
    };



    const __onValidValueEnter = (e: any, i: number) => {
        if (e.target.value.length === 1) {
            refEle.current?.[i + 1]?.focus()
        }
    }


    const __renderTitle = () => {
        return (
            <div className="popup-title">
                <h3 className="title">Passcode</h3>
                <button
                    className="close"
                    onClick={() => onClose()}
                ><img src={closeIcon} alt="Close icon" />
                </button>
            </div>
        );
    }

    const __handleEmailTokenRequest = () => {
        setResendRequest(true);

        let obj: any = {
            userToken: encodeURIComponent(tempToken)
        }

        dispatch(requestMFACodeOverEmailRequest(obj))
    }

    const __renderResendEmailCode = () => {

        if (contentType === ContentType.EMAIL_MFA) {
            return (
                <div className="resend-email-code pt-3">
                    <p className="text-center ">Unable to access your authenticator app?<br />
                        <button
                            type="button"
                            className="btn btn-link px-0"
                            onClick={() => __handleEmailTokenRequest()}
                            disabled={resendRequest}
                        >
                            Click here
                        </button> to be sent a one time login code</p>
                </div>
            );
        }
        return null;
    }

    // function to render the fields for the popup of passcode validation
    const __renderContent = () => {

        return (
            <div className="passcode-form-wrap">
                <p className="passcode-description">
                    { contentType === ContentType.EMAIL_MFA
                            ? "Please enter the 6 digit code from your Authenticator app"
                            : "Please enter the following characters from your passcode"
                    }
                </p>
                {Array.isArray(secondaryAuthenticationPart) &&
                    secondaryAuthenticationPart.length > 0 ? (
                    <form className="passcode-form"
                        onSubmit={(e) => __submitPassCode(e)}
                        ref={refEle}>
                        <div className="field-wrapper">
                            {secondaryAuthenticationPart.map(
                                (item: any, index: number) => {
                                    return (
                                        <div className={"filter-fields"} key={index}>
                                            {contentType === ContentType.PASSCODE ? <h3 className="title">{item}</h3> : null}
                                            <input type="text" name={item} id={"id" + item} onChange={(e) => __onValidValueEnter(e, index)} autoFocus={index === 0} />
                                        </div>
                                    );
                                }
                            )}
                        </div>

                        {__renderResendEmailCode()}
                        <button type="submit" className="submit">Submit</button>
                    </form>
                ) : null}


            </div>
        );
    };


    return (<div className="login-popup">
        <Popup
            header={__renderTitle()}
            content={__renderContent()}
        />
    </div>)
}

export default AuthIdentificationModal;